import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'

import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Button,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Box,
    Grid,
    Divider,
    CircularProgress,
} from '@material-ui/core'
import {
    Mail as MailIcon,
    Message as MessageSquareIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    MoreVert as MoreVerticalIcon,
    ArrowForward as ArrowRightIcon,
    Star as SparklesIcon,
    Search as SearchIcon,
    LocalOffer as ConcessionIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { Routes } from '../../../navigation/config/routes'
import { useCampaign } from '../../../hooks/Outreach/useCampaign'
import {
    CampaignStepType,
    CampaignStatus,
} from '../../../models/Outreach/types'

const useStyles = makeStyles((theme: Theme) => ({
    heroSection: {
        background: `linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)`,
        borderRadius: theme.spacing(4),
        padding: theme.spacing(4),
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    heroTitle: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    heroSubtitle: {
        fontSize: '1.125rem',
        opacity: 0.8,
    },
    createButton: {
        backgroundColor: 'white',
        color: '#3b82f6',
        fontSize: '1rem',
        fontWeight: 600,
        padding: '12px 24px',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            transform: 'scale(1.05)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
        boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    searchContainer: {
        marginTop: theme.spacing(3),
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: '200px',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            borderRadius: '8px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)',
        },
        '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255, 255, 255, 0.6)',
        },
    },
    filterButton: {
        color: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '6px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
        },
        '&.active': {
            backgroundColor: 'white',
            color: '#3b82f6',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
    },
    statsCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    statsCardContent: {
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    statsLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        fontWeight: 400,
    },
    statsValue: {
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: 1.2,
    },
    campaignCard: {
        position: 'relative',
        overflow: 'hidden',
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
    },
    statusIndicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
    },
    cardIcon: {
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(2),
        background: `linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))`,
        transition: 'transform 0.3s ease',
        '&:hover': {
            transform: 'scale(1.1)',
        },
    },
    metricsContainer: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        background: `linear-gradient(135deg, #f8fafc, #f1f5f9)`,
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2),
        textAlign: 'center',
    },
    viewButton: {
        background: `linear-gradient(135deg, #3b82f6, #1d4ed8)`,
        color: 'white',
        borderRadius: '8px',
        width: '100%',
        padding: theme.spacing(1.5),
        fontSize: '1rem',
        fontWeight: 600,
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            background: `linear-gradient(135deg, #2563eb, #1e40af)`,
        },
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
    },
    emptyStateIcon: {
        padding: theme.spacing(2),
        borderRadius: '50%',
        backgroundColor: '#f1f5f9',
        marginBottom: theme.spacing(2),
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(2),
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: theme.spacing(2),
        },
    },
    campaignsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
        },
    },
    cardHeader: {
        paddingBottom: theme.spacing(2),
    },
    divider: {
        margin: theme.spacing(1, 0),
        borderColor: 'rgba(226, 232, 240, 0.5)',
    },
}))

interface CampaignMenuProps {
    campaignId: number
    currentStatus: string
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
}

const CampaignMenu: React.FC<CampaignMenuProps> = ({
    campaignId,
    currentStatus,
    anchorEl,
    open,
    onClose,
}) => {
    const { updateCampaignStatus } = useCampaign()

    const handleResume = () => {
        updateCampaignStatus.mutate({
            id: campaignId,
            status: CampaignStatus.ACTIVE,
        })
        onClose()
    }

    const handlePause = () => {
        updateCampaignStatus.mutate({
            id: campaignId,
            status: CampaignStatus.PAUSED,
        })
        onClose()
    }

    const isPausedOrDraft =
        currentStatus.toLowerCase() === 'paused' ||
        currentStatus.toLowerCase() === 'draft' ||
        currentStatus === CampaignStatus.PAUSED ||
        currentStatus === CampaignStatus.ARCHIVED

    return (
        <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
            {isPausedOrDraft ? (
                <MenuItem onClick={handleResume}>
                    <PlayIcon fontSize="small" style={{ marginRight: 8 }} />
                    Resume
                </MenuItem>
            ) : (
                <MenuItem onClick={handlePause}>
                    <PauseIcon fontSize="small" style={{ marginRight: 8 }} />
                    Pause
                </MenuItem>
            )}
        </Menu>
    )
}

export const CampaignHome = () => {
    const classes = useStyles()
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
        null,
    )
    const { campaigns, isLoadingCampaigns, errorCampaigns } = useCampaign()

    // Transform backend campaigns to UI format
    const transformedCampaigns = useMemo(() => {
        if (!campaigns) return []

        return campaigns.map((campaign) => {
            // Determine campaign type based on steps
            const hasEmail = campaign.steps.some(
                (step) => step.step_type === CampaignStepType.EMAIL,
            )
            const hasSms = campaign.steps.some(
                (step) => step.step_type === CampaignStepType.TEXT_MESSAGE,
            )

            let type = 'Campaign'
            let icon = MailIcon
            let color = '#3b82f6'

            if (hasEmail && hasSms) {
                type = 'Email & SMS'
                icon = MailIcon
                color = '#3b82f6'
            } else if (hasEmail) {
                type = 'Email'
                icon = MailIcon
                color = '#3b82f6'
            } else if (hasSms) {
                type = 'SMS'
                icon = MessageSquareIcon
                color = '#8b5cf6'
            }

            // Map status from CampaignStatus enum or active boolean
            let status = 'Draft'
            if (campaign.status) {
                status =
                    campaign.status === CampaignStatus.ACTIVE
                        ? 'Active'
                        : campaign.status === CampaignStatus.PAUSED
                        ? 'Paused'
                        : campaign.status === CampaignStatus.DRAFT
                        ? 'Draft'
                        : 'Archived'
            } else if (campaign.active) {
                status = 'Active'
            }

            // Format created date
            const createdAt = campaign.created_date
                ? new Date(campaign.created_date).toISOString().split('T')[0]
                : 'Unknown'

            return {
                id: campaign.id,
                name: campaign.name,
                type: type,
                status: status,
                prospects: campaign.total_prospects || 0,
                sent: campaign.total_sent || 0,
                opened: campaign.total_opens || 0,
                clicked: campaign.total_clicks || 0,
                converted: campaign.total_converted || 0,
                createdAt: createdAt,
                icon: icon,
                color: color,
                concessionNames: campaign.concession_names || [],
            }
        })
    }, [campaigns])

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active':
                return 'primary'
            case 'Paused':
                return 'secondary'
            case 'Archived':
                return 'default'
            default:
                return 'default'
        }
    }

    const getStatusBackgroundColor = (status: string) => {
        switch (status) {
            case 'Active':
                return '#10b981' // green-500
            case 'Paused':
                return '#f59e0b' // amber-500
            case 'Archived':
                return '#6b7280' // gray-500
            default:
                return '#6b7280'
        }
    }

    const calculateRate = (numerator: number, denominator: number) => {
        if (denominator === 0) return '0%'
        return `${Math.round((numerator / denominator) * 100)}%`
    }

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        campaignId: number,
    ) => {
        setAnchorEl(event.currentTarget)
        setSelectedCampaignId(campaignId)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedCampaignId(null)
    }

    const filteredCampaigns = transformedCampaigns.filter((campaign) => {
        const matchesSearch =
            campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            campaign.type.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesStatus =
            statusFilter === 'all' ||
            campaign.status.toLowerCase() === statusFilter.toLowerCase()
        return matchesSearch && matchesStatus
    })

    const stats = {
        total: transformedCampaigns.length,
        active: transformedCampaigns.filter((c) => c.status === 'Active')
            .length,
        totalProspects: transformedCampaigns.reduce(
            (sum, c) => sum + c.prospects,
            0,
        ),
        avgOpenRate: calculateRate(
            transformedCampaigns.reduce((sum, c) => sum + c.opened, 0),
            transformedCampaigns.reduce((sum, c) => sum + c.sent, 0),
        ),
        conversions: transformedCampaigns.reduce(
            (sum, c) => sum + c.converted,
            0,
        ),
    }

    // Loading state
    if (isLoadingCampaigns) {
        return (
            <Box
                style={{
                    padding: 24,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    // Error state
    if (errorCampaigns) {
        return (
            <Box style={{ padding: 24 }}>
                <Typography variant="h6" color="error">
                    Failed to load campaigns
                </Typography>
            </Box>
        )
    }

    return (
        <Box style={{ padding: 24 }}>
            {/* Hero Section */}
            <Box className={classes.heroSection}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h3" className={classes.heroTitle}>
                            Campaigns
                        </Typography>
                        <Typography
                            variant="h6"
                            className={classes.heroSubtitle}
                        >
                            Create smart campaigns that convert prospects into
                            leads
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={4}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12,
                        }}
                    >
                        <Link
                            to={Routes.manage.campaign.guestCards}
                            style={{ textDecoration: 'none' }}
                        >
                            <Button
                                variant="outlined"
                                style={{
                                    color: 'white',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                }}
                            >
                                Guest Cards
                            </Button>
                        </Link>
                        <Link
                            to={Routes.manage.campaign.create}
                            style={{ textDecoration: 'none' }}
                        >
                            <Button
                                className={classes.createButton}
                                startIcon={<SparklesIcon />}
                            >
                                Create Campaign
                            </Button>
                        </Link>
                    </Grid>
                </Grid>

                {/* Search & Filter */}
                <Box className={classes.searchContainer}>
                    <TextField
                        className={classes.searchField}
                        placeholder="Search campaigns..."
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setSearchQuery(e.target.value)
                        }
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box display="flex" style={{ gap: 8 }}>
                        {['all', 'Active', 'Paused', 'Archived'].map(
                            (filter) => (
                                <Button
                                    key={filter}
                                    className={`${classes.filterButton} ${
                                        statusFilter === filter ? 'active' : ''
                                    }`}
                                    onClick={() => setStatusFilter(filter)}
                                    size="small"
                                >
                                    {filter === 'all' ? 'All' : filter}
                                </Button>
                            ),
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Quick Stats */}
            <Box className={classes.statsGrid}>
                <Card className={classes.statsCard}>
                    <CardContent className={classes.statsCardContent}>
                        <Typography className={classes.statsLabel}>
                            Total Campaigns
                        </Typography>
                        <Typography
                            className={classes.statsValue}
                            style={{ color: '#3b82f6' }}
                        >
                            {stats.total}
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                    <CardContent className={classes.statsCardContent}>
                        <Typography className={classes.statsLabel}>
                            Active Now
                        </Typography>
                        <Typography
                            className={classes.statsValue}
                            style={{ color: '#10b981' }}
                        >
                            {stats.active}
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                    <CardContent className={classes.statsCardContent}>
                        <Typography className={classes.statsLabel}>
                            Total Reach
                        </Typography>
                        <Typography
                            className={classes.statsValue}
                            style={{ color: '#1f2937' }}
                        >
                            {stats.totalProspects.toLocaleString()}
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                    <CardContent className={classes.statsCardContent}>
                        <Typography className={classes.statsLabel}>
                            Avg Open Rate
                        </Typography>
                        <Typography
                            className={classes.statsValue}
                            style={{ color: '#8b5cf6' }}
                        >
                            {stats.avgOpenRate}
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                    <CardContent className={classes.statsCardContent}>
                        <Typography className={classes.statsLabel}>
                            Conversions
                        </Typography>
                        <Typography
                            className={classes.statsValue}
                            style={{ color: '#f59e0b' }}
                        >
                            {stats.conversions}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Campaign Cards Grid */}
            {filteredCampaigns.length > 0 ? (
                <Box className={classes.campaignsGrid}>
                    {filteredCampaigns.map((campaign) => {
                        const Icon = campaign.icon
                        const isSmsOnly = campaign.type === 'SMS'
                        const openRate = calculateRate(
                            campaign.opened,
                            campaign.sent,
                        )
                        const clickRate = calculateRate(
                            campaign.clicked,
                            campaign.sent,
                        )
                        const conversionRate = calculateRate(
                            campaign.converted,
                            campaign.sent,
                        )

                        return (
                            <Card
                                key={campaign.id}
                                className={classes.campaignCard}
                            >
                                {/* Status Indicator Bar */}
                                <Box
                                    className={classes.statusIndicator}
                                    style={{
                                        backgroundColor: getStatusBackgroundColor(
                                            campaign.status,
                                        ),
                                    }}
                                />

                                <CardHeader
                                    className={classes.cardHeader}
                                    title={
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 16 }}
                                                flex={1}
                                            >
                                                <Box
                                                    className={classes.cardIcon}
                                                >
                                                    <Icon
                                                        style={{
                                                            color:
                                                                campaign.color,
                                                            fontSize: 24,
                                                        }}
                                                    />
                                                </Box>
                                                <Box flex={1} minWidth={0}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        style={{
                                                            gap: 8,
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            style={{
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                            noWrap
                                                        >
                                                            {campaign.name}
                                                        </Typography>
                                                        <Chip
                                                            label={
                                                                campaign.status
                                                            }
                                                            color={
                                                                getStatusColor(
                                                                    campaign.status,
                                                                ) as
                                                                    | 'primary'
                                                                    | 'default'
                                                                    | 'secondary'
                                                            }
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {campaign.type} •{' '}
                                                        {campaign.prospects.toLocaleString()}{' '}
                                                        prospects
                                                    </Typography>
                                                    {campaign.concessionNames
                                                        ?.length > 0 &&
                                                        campaign.concessionNames.map(
                                                            (
                                                                name: string,
                                                                i: number,
                                                            ) => (
                                                                <Chip
                                                                    key={i}
                                                                    icon={
                                                                        <ConcessionIcon
                                                                            style={{
                                                                                fontSize: 14,
                                                                                color:
                                                                                    '#047857',
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={name}
                                                                    size="small"
                                                                    style={{
                                                                        backgroundColor:
                                                                            '#d1fae5',
                                                                        color:
                                                                            '#047857',
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.625rem',
                                                                        height: 22,
                                                                        marginTop: 4,
                                                                        borderRadius: 6,
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Created{' '}
                                                        {new Date(
                                                            campaign.createdAt,
                                                        ).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    handleMenuOpen(
                                                        e,
                                                        campaign.id,
                                                    )
                                                }}
                                            >
                                                <MoreVerticalIcon />
                                            </IconButton>
                                        </Box>
                                    }
                                />

                                <CampaignMenu
                                    campaignId={campaign.id}
                                    currentStatus={campaign.status}
                                    anchorEl={anchorEl}
                                    open={
                                        Boolean(anchorEl) &&
                                        selectedCampaignId === campaign.id
                                    }
                                    onClose={handleMenuClose}
                                />

                                <CardContent>
                                    {/* Performance Metrics */}
                                    <Box className={classes.metricsContainer}>
                                        <Box
                                            className={classes.metricsGrid}
                                            style={{
                                                gridTemplateColumns: isSmsOnly
                                                    ? 'repeat(2, 1fr)'
                                                    : 'repeat(3, 1fr)',
                                            }}
                                        >
                                            {!isSmsOnly && (
                                                <>
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Open Rate
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            style={{
                                                                color:
                                                                    '#3b82f6',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {openRate}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {campaign.opened}{' '}
                                                            opens
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        style={{
                                                            borderLeft:
                                                                '1px solid rgba(226, 232, 240, 0.5)',
                                                            borderRight:
                                                                '1px solid rgba(226, 232, 240, 0.5)',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Click Rate
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            style={{
                                                                color:
                                                                    '#8b5cf6',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {clickRate}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {campaign.clicked}{' '}
                                                            clicks
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                            {isSmsOnly && (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Total Sent
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            color: '#3b82f6',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {campaign.sent.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box
                                                style={{
                                                    borderLeft: !isSmsOnly
                                                        ? '1px solid rgba(226, 232, 240, 0.5)'
                                                        : 'none',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    Conversions
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    style={{
                                                        color: '#10b981',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {campaign.converted}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {conversionRate} rate
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider className={classes.divider} />

                                    {/* CTA */}
                                    <Link
                                        to={Routes.manage.campaign.detail.replace(
                                            ':id',
                                            campaign.id.toString(),
                                        )}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Button
                                            className={classes.viewButton}
                                            endIcon={<ArrowRightIcon />}
                                        >
                                            View Campaign Details
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )
                    })}
                </Box>
            ) : (
                <Card className={classes.statsCard}>
                    <CardContent className={classes.emptyStateContainer}>
                        <Box className={classes.emptyStateIcon}>
                            <MailIcon
                                style={{ fontSize: 32, color: '#94a3b8' }}
                            />
                        </Box>
                        <Typography variant="h6" gutterBottom>
                            No campaigns found
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="center"
                            paragraph
                        >
                            {searchQuery
                                ? 'Try adjusting your search or filters'
                                : 'Create your first campaign to get started'}
                        </Typography>
                        {!searchQuery && (
                            <Link
                                to={Routes.manage.campaign.create}
                                style={{ textDecoration: 'none' }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SparklesIcon />}
                                    style={{
                                        background: `linear-gradient(135deg, #3b82f6, #1d4ed8)`,
                                    }}
                                >
                                    Create Your First Campaign
                                </Button>
                            </Link>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    )
}
